import { format } from 'date-fns';

import type {
  CheckpointConfigurationStatusDto,
  CheckpointSettingDto,
  CheckpointStatusWindowDto,
  SdkCheckpointMetadataDto,
} from '@/domains/siteCompliance';
import {
  makeCheckpointKey,
  normalizeCheckpointId,
} from '@/lib/checkpointKeys';
import type { CheckpointSystemStatus } from '@/types';

import type {
  SiteCheckpointRow,
  SiteMaintenanceCriticality,
} from './SiteCheckpointSettingsPage.types';

export const MAINTENANCE_CRITICALITY_OPTIONS: Array<{
  value: SiteMaintenanceCriticality;
  label: string;
  days: number;
}> = [
  { value: 'CRITICAL', label: 'Critical - 24 hours', days: 1 },
  { value: 'HIGH', label: 'High - 3 days', days: 3 },
  { value: 'MEDIUM', label: 'Medium - 7 days', days: 7 },
];

export const toDate = (value?: string | Date | null) => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toLocalDateTimeInput = (value?: string | Date | null) => {
  const parsed = toDate(value);
  if (!parsed) return '';
  return format(parsed, "yyyy-MM-dd'T'HH:mm");
};

export const getCurrentLocalDateTimeInput = () => {
  return toLocalDateTimeInput(new Date());
};

export const isPastDateTimeInput = (value?: string) => {
  const parsed = toDate(value);
  if (!parsed) return false;

  const now = new Date();
  now.setSeconds(0, 0);
  return parsed.getTime() < now.getTime();
};

export const isTransitionAllowed = (
  from: CheckpointSystemStatus,
  to: CheckpointSystemStatus,
) => {
  if (from === to) return false;
  if (to === 'MAINTENANCE') return from === 'LIVE';
  if (from === 'MAINTENANCE' && to === 'PREVIEW') return false;

  return true;
};

export const CHECKPOINT_STATUS_MAP: Record<
  string,
  { label: string; color: string }
> = {
  LIVE: {
    label: 'LIVE',
    color: 'green',
  },
  PREVIEW: {
    label: 'PREVIEW',
    color: 'orange',
  },
  MAINTENANCE: {
    label: 'MAINTENANCE',
    color: 'red',
  },
  DISABLED: {
    label: 'DISABLED',
    color: 'gray',
  },
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readString = (...values: unknown[]) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = `${value}`.trim();
    if (text) return text;
  }

  return '';
};

const readBoolean = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }

  return undefined;
};

const readStatusWindow = (
  value: unknown,
): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined;
  return value;
};

const readWindowField = (
  window: Record<string, unknown> | undefined,
  camelKey: string,
  snakeKey?: string,
) => window?.[camelKey] ?? (snakeKey ? window?.[snakeKey] : undefined);

const hasActiveWindow = (value: unknown) => {
  if (value === true) return true;
  if (!value || typeof value !== 'object') return false;

  const record = value as Record<string, unknown>;
  if (typeof record.enabled === 'boolean') return record.enabled;

  const fields = [
    'reason',
    'startDate',
    'start_date',
    'endDate',
    'end_date',
    'type',
    'unscoredType',
    'unscored_type',
  ];

  return fields.some((field) => {
    const current = record[field];
    if (typeof current === 'string') return current.trim().length > 0;
    return current !== null && current !== undefined;
  });
};

export const normalizeCheckpointStatus = (
  checkpoint: CheckpointSettingDto | SiteCheckpointRow,
): CheckpointSystemStatus => {
  const checkpointRecord = checkpoint as Record<string, unknown>;
  const enabledValue = readBoolean(
    checkpointRecord.enabled ?? checkpointRecord.isEnabled,
  );

  if (enabledValue === false) return 'DISABLED';

  const statusField = checkpointRecord.status;
  if (statusField && typeof statusField === 'object') {
    const statusObj = statusField as CheckpointConfigurationStatusDto;
    const disabled = statusObj.disabled;
    const unScored = (statusObj.unScored ?? statusObj.unscored) as
      | CheckpointStatusWindowDto
      | Record<string, unknown>
      | undefined;

    if (hasActiveWindow(unScored)) {
      const unScoredRecord = readStatusWindow(unScored);
      const typeRaw = readString(
        unScoredRecord?.type,
        unScoredRecord?.unscoredType,
        unScoredRecord?.unscored_type,
      ).toLowerCase();

      if (typeRaw) {
        if (typeRaw === 'maintenance') {
          const endDateRaw = readString(
            readWindowField(unScoredRecord, 'endDate', 'end_date'),
          );
          const endMs = endDateRaw ? new Date(endDateRaw).getTime() : Number.NaN;

          if (
            !Number.isNaN(endMs) &&
            endMs <= Date.now() &&
            hasActiveWindow(disabled)
          ) {
            return 'DISABLED';
          }

          return 'MAINTENANCE';
        }

        return 'PREVIEW';
      }

      const reasonRaw = readString(
        unScoredRecord?.reason,
        unScoredRecord?.Reason,
      ).toLowerCase();
      if (reasonRaw) {
        if (reasonRaw.includes('maintenance')) {
          const endDateRaw = readString(
            readWindowField(unScoredRecord, 'endDate', 'end_date'),
          );
          const endMs = endDateRaw ? new Date(endDateRaw).getTime() : Number.NaN;

          if (
            !Number.isNaN(endMs) &&
            endMs <= Date.now() &&
            hasActiveWindow(disabled)
          ) {
            return 'DISABLED';
          }

          return 'MAINTENANCE';
        }

        return 'PREVIEW';
      }
    }

    if (hasActiveWindow(disabled)) {
      return 'DISABLED';
    }
  }

  const raw = readString(
    checkpointRecord.state,
    checkpointRecord.mode,
    checkpointRecord.releaseStatus,
  ).toUpperCase();

  switch (raw) {
    case 'PREVIEW':
      return 'PREVIEW';
    case 'MAINTENANCE':
      return 'MAINTENANCE';
    case 'DISABLED':
      return 'DISABLED';
    default:
      return 'LIVE';
  }
};

export const isScoredFromStatus = (status: CheckpointSystemStatus) => {
  return status === 'LIVE';
};

export const formatStatusDate = (iso?: string) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'yyyy-MM-dd HH:mm');
};

export const getStatusTooltipLines = (row?: SiteCheckpointRow | null) => {
  if (!row) return [];
  const status = (row.status ?? 'LIVE').toString();
  if (status === 'LIVE') return [];

  const tooltip = row.statusTooltip;

  const cleanReason = (value?: string) =>
    (value ?? '')
      .toString()
      .replace(/-\s*maintenance\s*$/i, '')
      .trim();

  const reasonText = cleanReason(tooltip?.reason);
  const startText = formatStatusDate(tooltip?.startDate);
  const endText = formatStatusDate(tooltip?.endDate);

  const lines: string[] = [];
  if (reasonText) lines.push(`Reason: ${reasonText}`);
  if (startText) lines.push(`Start: ${startText}`);
  if (endText) lines.push(`End: ${endText}`);
  return lines;
};

const pickArray = <T,>(payload: unknown): T[] => {
  const source = payload as Record<string, unknown> | undefined;
  const candidates = [
    payload,
    source?.data,
    (source?.data as Record<string, unknown> | undefined)?.data,
    source?.items,
    (source?.data as Record<string, unknown> | undefined)?.items,
    source?.checkpoints,
    (source?.data as Record<string, unknown> | undefined)?.checkpoints,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as T[];
  }

  return [];
};

const getConfigCheckpointId = (cfg: CheckpointSettingDto) => {
  const anyCfg = cfg as Record<string, unknown>;
  return readString(
    anyCfg?.checkpointId,
    anyCfg?.checkpointID,
    anyCfg?.checkpoint_id,
    anyCfg?.id,
  );
};

const getConfigCheckpointVersion = (cfg: CheckpointSettingDto) => {
  const anyCfg = cfg as Record<string, unknown>;
  return (
    anyCfg?.checkpointVersion ??
    anyCfg?.checkpoint_version ??
    anyCfg?.version ??
    anyCfg?.Version ??
    null
  );
};

const getCatalogCheckpointId = (catalog: SdkCheckpointMetadataDto) => {
  const rawId = readString(catalog.id);
  const version = readString(catalog.checkpointVersion);
  const suffix = version ? `.${version}` : '';

  if (suffix && rawId.endsWith(suffix)) {
    return rawId.slice(0, -suffix.length);
  }

  return rawId;
};

const getCatalogCheckpointVersion = (catalog: SdkCheckpointMetadataDto) =>
  catalog.checkpointVersion ?? null;

const buildCatalogMaps = (catalogData: unknown) => {
  const catalog = pickArray<SdkCheckpointMetadataDto>(
    (catalogData as Record<string, unknown> | undefined)?.data ?? catalogData,
  );

  const byKey = new Map(
    catalog
      .map((item) => {
        const id = getCatalogCheckpointId(item);
        const version = getCatalogCheckpointVersion(item);
        return [makeCheckpointKey(id, version), item] as const;
      })
      .filter(([key]) => Boolean(key.split('::')[0])),
  );
  const byId = new Map(
    catalog
      .map((item) => [normalizeCheckpointId(getCatalogCheckpointId(item)), item] as const)
      .filter(([id]) => Boolean(id)),
  );

  return { byId, byKey };
};

const readStatusTooltip = (
  config: CheckpointSettingDto,
  status: CheckpointSystemStatus,
) => {
  const statusObj = config.status;
  if (!statusObj || typeof statusObj !== 'object') return undefined;

  const disabled = readStatusWindow(statusObj.disabled);
  const unScored = readStatusWindow(statusObj.unScored ?? statusObj.unscored);

  const read = (window: Record<string, unknown> | undefined) => {
    if (!window || typeof window !== 'object') return {};
    return {
      reason: readString(window.reason, window.note, window.message) || undefined,
      startDate:
        readString(readWindowField(window, 'startDate', 'start_date')) ||
        undefined,
      endDate:
        readString(readWindowField(window, 'endDate', 'end_date')) || undefined,
    } as { reason?: string; startDate?: string; endDate?: string };
  };

  if (status === 'DISABLED') {
    return read(disabled);
  }

  if (status === 'PREVIEW') {
    return read(unScored);
  }

  if (status === 'MAINTENANCE') {
    const unscoredRead = read(unScored);
    const disabledRead = read(disabled);
    return {
      reason: unscoredRead.reason,
      startDate: disabledRead.startDate ?? unscoredRead.startDate,
      endDate: unscoredRead.endDate,
    };
  }

  return undefined;
};

export const buildCheckpointRows = (
  checkpointConfigsData: unknown,
  catalogData: unknown,
) => {
  if (!checkpointConfigsData) return [];

  const configs = pickArray<CheckpointSettingDto>(
    (checkpointConfigsData as Record<string, unknown> | undefined)?.data ??
      checkpointConfigsData,
  );
  const catalogMaps = buildCatalogMaps(catalogData);

  return configs
    .slice()
    .map((config) => {
      const checkpointId = getConfigCheckpointId(config);
      const checkpointVersion = getConfigCheckpointVersion(config);
      const hasVersion =
        checkpointVersion !== null &&
        checkpointVersion !== undefined &&
        `${checkpointVersion}`.trim() !== '';
      const catalog = hasVersion
        ? catalogMaps.byKey.get(makeCheckpointKey(checkpointId, checkpointVersion))
        : catalogMaps.byId.get(normalizeCheckpointId(checkpointId));
      const status = normalizeCheckpointStatus(config);
      const enabled = readBoolean(config.isEnabled ?? (config as { enabled?: unknown }).enabled);

      return {
        accountable: readString(config.accountable, catalog?.accountable),
        category: readString(config.category, catalog?.category),
        description: readString(config.description, catalog?.description),
        displayName: readString(
          config.name,
          (config as { displayName?: unknown }).displayName,
          catalog?.displayName,
          checkpointId,
        ),
        enabled: enabled ?? status !== 'DISABLED',
        id: checkpointId,
        isScored: isScoredFromStatus(status),
        owner: readString(config.accountable, catalog?.accountable, 'Network'),
        referenceSystem: readString(config.referenceSystem, catalog?.referenceSystem),
        status,
        statusTooltip: readStatusTooltip(config, status),
        version:
          checkpointVersion === null || checkpointVersion === undefined
            ? undefined
            : (checkpointVersion as string | number),
      } satisfies SiteCheckpointRow;
    })
    .sort((left, right) => {
      const byName = left.displayName.localeCompare(right.displayName);
      if (byName !== 0) return byName;
      return `${left.version ?? ''}`.localeCompare(`${right.version ?? ''}`);
    });
};
